import { useQuery } from "@apollo/client/react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link, useLocation, useParams } from "react-router";
import { useHasFeature } from "@/auth/access-features";
import { FEATURE_CATALOG } from "@/auth/feature-catalog";
import { BackButton } from "@/components/common/BackButton";
import { buttonClasses } from "@/components/design/button-classes";
import {
  SelfAssessmentResultDocument,
} from "@/graphql/generated/operations";
import type { UserRank } from "@/graphql/generated/types";
import { ELEMENT_CODES, elementDisplayName, elementNameId } from "@/lib/elements";
import { elementEvaluation, GROW_EXAMPLE_COUNT } from "@/lib/element-evaluations";
import { AskWithConsent } from "@/components/grow/AskWithConsent";
import { useGrowImproveContext } from "@/lib/use-grow-improve-context";
import { growFollowUps } from "@/lib/grow-assistant-context";
import { achievementRankText, rankLabel } from "@/lib/grow-rank";

// Grow · self-assessment result screen (own route /grow/:elementId/result).
// Self-assessment result screen: the computed score, the banded evaluation
// text (lib/element-evaluations, sourced from the
// bd-requirements#3 corpus), and the achievement deltas (gained score / new rank)
// against the pre-save snapshot the assessment page hands over via router state.

type ResultNavState = { prevScore?: number | null; prevRank?: UserRank | null } | null;

function Medal({ size = 28 }: { size?: number }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      className="text-ink-accent"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <circle cx="12" cy="8" r="6" />
      <path d="M8.5 13.5 7 22l5-3 5 3-1.5-8.5" />
    </svg>
  );
}

export function ElementResultPage() {
  const intl = useIntl();
  // Reached through Play, so Map may be switched off for the viewer.
  const canMap = useHasFeature(FEATURE_CATALOG.growMap.key);
  const params = useParams<{ elementId: string }>();
  const element = params.elementId ?? "";
  const valid = element in ELEMENT_CODES;

  const { state } = useLocation();
  const nav = state as ResultNavState;

  const { data, loading } = useQuery(SelfAssessmentResultDocument, {
    variables: { element: element as never },
    skip: !valid,
    // After a save the cached values are stale; always pull the recomputed score.
    fetchPolicy: "cache-and-network",
  });

  const evalResult = data?.selfAssessmentResult;
  const ranking = data?.growRanking;
  const score = evalResult?.result ?? null;
  const resultText = evalResult?.resultText ?? null;
  const evaluation = elementEvaluation(element, score, intl.locale);
  const completed =
    !!evalResult &&
    evalResult.questionCount > 0 &&
    evalResult.answeredQuestionCount === evalResult.questionCount;

  const powerName = valid
    ? intl.formatMessage({ id: elementNameId(element), defaultMessage: elementDisplayName(element) })
    : element;
  const { items, baseItems, contextPending, consented, onConsent, qState } = useGrowImproveContext({
    // `valid`, not `element`: on this route `element` is always a non-empty string, so a truthy
    // check would let /grow/NOT_AN_ELEMENT/result fire with an invalid enum.
    element: valid ? element : null,
    powerName,
    score,
    answered: evalResult?.answeredQuestionCount ?? 0,
    total: evalResult?.questionCount ?? 0,
    bandLabel: resultText,
    scoreLoading: loading,
  });

  if (!valid) {
    return (
      <section className="mx-auto max-w-screen-md px-4 py-12" data-graphql="SelfAssessmentResult, SelfAssessmentStatements, SelfAssessmentSavedAnswers, Myself">
        <h1 id="grow-unknown-element" className="smycles-page-title">
          <FormattedMessage id="grow.elementResult.unknownElement" defaultMessage="Unknown element" />
        </h1>
        <p className="mt-2 text-smycles-body text-ink-muted">{element}</p>
      </section>
    );
  }

  // growRanking.score is a global completion percentage across all elements,
  // not this element's result - next to "Dein Score" it reads as a second,
  // worse grade. Only the rank delta is shown here; the overall score lives on
  // the Power Map.
  const rankChanged =
    !!ranking && nav?.prevRank != null && ranking.userRank !== nav.prevRank;
  const code = ELEMENT_CODES[element];

  const navButtons = (
    <>
      {/* Both depend on the Power Map feature, per role (main 0b332106): without it neither page
          exists for this person, so the buttons would lead nowhere. */}
      {canMap ? (
        <Link to="/grow/map" className={buttonClasses({ variant: "primary" })}>
          <FormattedMessage id="grow.elementResult.toPowerMap" defaultMessage="To Power Map" />
        </Link>
      ) : null}
      {canMap && code ? (
        <Link to={`/grow/power/${code}`} className={buttonClasses({ variant: "primary" })}>
          <FormattedMessage id="grow.elementResult.toPower" defaultMessage="To Power" />
        </Link>
      ) : null}
      <Link
        id="grow-result-reassess"
        to={`/grow/${element}`}
        className={buttonClasses({ variant: "secondary" })}
      >
        <FormattedMessage id="grow.elementResult.reassess" defaultMessage="Assess again" />
      </Link>
    </>
  );

  return (
    <section className="mx-auto max-w-screen-md px-4 py-12" data-graphql="SelfAssessmentResult, SelfAssessmentStatements, SelfAssessmentSavedAnswers, Myself">
      <div className="mb-5">
        <BackButton />
      </div>

      <header className="mb-8 text-center">
        <h1 className="smycles-page-title">{intl.formatMessage({ id: elementNameId(element), defaultMessage: elementDisplayName(element) })}</h1>
        <p id="grow-result-completed" className="mt-2 text-smycles-meta tracking-wide text-ink-muted">
          <FormattedMessage
            id="grow.elementResult.selfAssessmentCompleted"
            defaultMessage="All statements rated"
          />
        </p>
      </header>

      <div id="grow-result" className="box bg-surface p-8 text-center">
        <div id="grow-result-scorelabel" className="smycles-field-label text-ink-muted">
          <FormattedMessage id="grow.elementResult.yourScore" defaultMessage="Your score" />
        </div>
        <div id="grow-result-score" className="mt-1 font-serif text-smycles-display-xl leading-none tabular-nums">
          {score ?? (loading ? "…" : "–")}
        </div>
        {resultText ? (
          <div className="smycles-field-label mt-2 text-ink">{resultText}</div>
        ) : null}
        <div className="mt-4 flex items-center justify-center gap-2 text-smycles-meta text-ink-muted">
          {completed ? (
            <>
              <Medal size={20} />
              <span id="grow-result-fully">
                <FormattedMessage
                  id="grow.elementResult.fullyAnswered"
                  defaultMessage="Fully answered"
                />
              </span>
            </>
          ) : evalResult ? (
            <span>
              <FormattedMessage
                id="grow.elementResult.answeredCount"
                defaultMessage="{answered}/{total} answered"
                values={{
                  answered: evalResult.answeredQuestionCount,
                  total: evalResult.questionCount,
                }}
              />
            </span>
          ) : null}
        </div>
      </div>

      {evaluation ? (
        <div className="box mt-6 bg-surface p-6">
          <div className="smycles-field-label text-ink-muted">
            <FormattedMessage id="grow.elementResult.evaluation" defaultMessage="Evaluation" />
          </div>
          <p className="mt-2 text-smycles-body leading-relaxed text-ink">{evaluation}</p>
        </div>
      ) : null}

      {rankChanged && ranking ? (
        <div className="mt-6">
          <div className="box-flat flex items-center gap-3 bg-surface px-4 py-3">
            <Medal />
            <span className="text-smycles-body text-ink">
              {achievementRankText(rankLabel(ranking.userRank, intl.locale), intl.locale)}
            </span>
          </div>
        </div>
      ) : null}

      {evalResult?.lastEvaluationOn ? (
        <p className="mt-6 text-center text-smycles-label text-ink-muted">
          <FormattedMessage
            id="grow.elementResult.lastEvaluatedOn"
            defaultMessage="Last evaluated: {date}"
            values={{
              date: intl.formatDate(evalResult.lastEvaluationOn, {
                year: "numeric",
                month: "long",
                day: "numeric",
              }),
            }}
          />
        </p>
      ) : null}

      {/* One block, not two rows: the trigger is the first button of the row, and the consent
          notice below spans exactly that row (left edge of the trigger to right edge of "Assess
          again"), which is what AskWithConsent's `center` alignment shrink-wraps to. */}
      <div className="mt-8 flex justify-center">
        {score != null ? (
          <AskWithConsent
            key={element}
            align="center"
            variant="primary"
            powerName={powerName}
            items={items ?? baseItems}
                personal={items != null}
                disabled={contextPending}
            consented={consented}
            onConsent={onConsent}
            // What the person sees in their own bubble: the one sentence they would have typed.
            // It asks how to develop the power, not how to raise a score, so it holds whether
            // the questionnaire is finished, half done or not started.
            prompt={() => intl.formatMessage({ id: "grow.ask.improveAsk" }, { name: powerName })}
            // The brief rides along hidden. It is not the answer contract - that lives in the
            // GROWTH section prompt - only what the model cannot derive: the example count.
            // Below the completeness bar it must not ask for the score evaluation either, or
            // the question in the person's own turn would override the section prompt.
            followUps={() => growFollowUps((id) => intl.formatMessage({ id }))}
                hiddenContext={() =>
              intl.formatMessage(
                { id: qState === "complete" ? "grow.ask.improveBrief" : "grow.ask.improveBriefIncomplete" },
                { name: powerName, score, examples: GROW_EXAMPLE_COUNT },
              )
            }
            label={<FormattedMessage id="grow.elementResult.askButton" defaultMessage="Discuss my result" />}
          >
            {navButtons}
          </AskWithConsent>
        ) : (
          <div className="flex flex-wrap items-center justify-center gap-3">{navButtons}</div>
        )}
      </div>

    </section>
  );
}
